import { tokens } from '@/design';
import type { ResizeHandleId } from './elementGeometry';
import { RESIZE_HANDLES } from './elementGeometry';
import { mmToScreenPx } from './pageUnits';
import type { PageRect } from './layoutTypes';
import type { PointerEvent, ReactNode } from 'react';

const HANDLE_CURSOR: Record<ResizeHandleId, string> = {
  nw: 'nwse-resize',
  se: 'nwse-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
};

export interface LayoutElementChromeProps {
  readonly rect: PageRect;
  readonly pageZoom: number;
  readonly selected: boolean;
  readonly label: string;
  readonly onSelect: (event?: PointerEvent<HTMLDivElement>) => void;
  readonly onMovePointerDown: (event: PointerEvent<HTMLDivElement>) => void;
  readonly onResizePointerDown: (handle: ResizeHandleId, event: PointerEvent<HTMLSpanElement>) => void;
  readonly onRotatePointerDown?: (event: PointerEvent<HTMLSpanElement>) => void;
  readonly onDoubleClick?: () => void;
  readonly children: ReactNode;
  /** When true, only the top strip starts a move so the map stays interactive. */
  readonly moveFromHandleOnly?: boolean;
  readonly locked?: boolean;
  /** Fitting strategy owns width; retain only north/south height handles. */
  readonly lockHorizontalResize?: boolean;
  readonly 'data-testid'?: string;
}

export function LayoutElementChrome({
  rect,
  pageZoom,
  selected,
  label,
  onSelect,
  onMovePointerDown,
  onResizePointerDown,
  onRotatePointerDown,
  onDoubleClick,
  children,
  moveFromHandleOnly = false,
  locked = false,
  lockHorizontalResize = false,
  'data-testid': testId,
}: LayoutElementChromeProps) {
  const left = mmToScreenPx(rect.x, pageZoom);
  const top = mmToScreenPx(rect.y, pageZoom);
  const width = mmToScreenPx(rect.width, pageZoom);
  const height = mmToScreenPx(rect.height, pageZoom);
  const handlePx = 8;

  return (
    <div
      data-testid={testId}
      role={moveFromHandleOnly ? 'group' : 'button'}
      tabIndex={0}
      aria-label={label}
      aria-pressed={moveFromHandleOnly ? undefined : selected}
      onPointerDown={(event) => {
        event.stopPropagation();
        onSelect(event);
        if (!moveFromHandleOnly && !locked) onMovePointerDown(event);
      }}
      onDoubleClick={onDoubleClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      style={{
        position: 'absolute',
        left,
        top,
        width,
        height,
        boxSizing: 'border-box',
        outline: selected
          ? `${tokens.focus.ringWidth}px solid ${tokens.color.accent.primary}`
          : `${tokens.border.width.thin}px solid ${tokens.color.ink.muted}`,
        cursor: moveFromHandleOnly || locked ? 'default' : 'move',
        overflow: 'visible',
        background: 'transparent',
      }}
    >
      {moveFromHandleOnly ? (
        <div
          data-testid="layout-element-move-handle"
          onPointerDown={(event) => {
            event.stopPropagation();
            onSelect();
            onMovePointerDown(event);
          }}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: -14,
            height: 14,
            background: tokens.color.accent.subtle,
            color: tokens.color.ink.primary,
            fontSize: 9,
            fontFamily: tokens.font.family.ui,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: tokens.space[1],
            cursor: 'move',
            zIndex: 3,
          }}
        >
          {label}
        </div>
      ) : null}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'auto',
        }}
      >
        {children}
      </div>
      {selected && !locked
        ? RESIZE_HANDLES.filter(
            (handle) =>
              !lockHorizontalResize
              || (!handle.includes('e') && !handle.includes('w')),
          ).map((handle) => {
            const vertical = handle.includes('n')
              ? 0
              : handle.includes('s')
                ? height - handlePx
                : height / 2 - handlePx / 2;
            const horizontal = handle.includes('w')
              ? 0
              : handle.includes('e')
                ? width - handlePx
                : width / 2 - handlePx / 2;
            return (
              <span
                key={handle}
                data-testid={`layout-handle-${handle}`}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  onResizePointerDown(handle, event);
                }}
                style={{
                  position: 'absolute',
                  left: horizontal,
                  top: vertical,
                  width: handlePx,
                  height: handlePx,
                  background: tokens.color.accent.primary,
                  border: `${tokens.border.width.thin}px solid ${tokens.color.surface[0]}`,
                  cursor: HANDLE_CURSOR[handle],
                  zIndex: 2,
                }}
              />
            );
          })
        : null}
      {selected && !locked && onRotatePointerDown ? (
        <span
          data-testid="layout-rotation-handle"
          role="button"
          tabIndex={0}
          aria-label={`Rotate ${label}`}
          onPointerDown={(event) => {
            event.stopPropagation();
            onRotatePointerDown(event);
          }}
          style={{
            position: 'absolute',
            left: width / 2 - 6,
            top: -24,
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: tokens.color.accent.primary,
            border: `${tokens.border.width.thin}px solid ${tokens.color.surface[0]}`,
            cursor: 'grab',
            zIndex: 4,
          }}
        />
      ) : null}
    </div>
  );
}
